import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, Loader2, Mail, MousePointerClick, Save, ShoppingCart, Smartphone, Ticket, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Interruttore } from './Interruttore';
import { Pannello } from './Pannello';
import { SceltaEtichetta } from './SceltaEtichetta';
import { leggiProdotti, validaImpostazioni } from '../../lib/eventi/impostazioni';
import type { Etichetta, Evento, ImpostazioniEvento } from '../../lib/eventi/tipi';

/** Aprire e chiudere le richieste si fa dalla testata della pagina, non da qui. */
export type CampiImpostazioni = Omit<ImpostazioniEvento, 'scelte_aperte'>;

type Props = {
  evento: Evento;
  etichette: Etichetta[];
  onSalva: (campi: CampiImpostazioni) => Promise<void>;
};

const daEvento = (e: Evento): CampiImpostazioni => ({
  nome: e.nome,
  lista_id: e.lista_id,
  etichetta_richiesta_id: e.etichetta_richiesta_id,
  etichetta_assegnata_id: e.etichetta_assegnata_id,
  etichetta_rimanda_link_id: e.etichetta_rimanda_link_id,
  url_scelta: e.url_scelta,
  url_app: e.url_app,
  email_riferimento: e.email_riferimento,
  prodotti_sito: e.prodotti_sito ?? [],
  url_registrazione: e.url_registrazione,
  etichetta_acquisto_id: e.etichetta_acquisto_id,
  etichetta_registrati_id: e.etichetta_registrati_id,
  biglietti_pdf: e.biglietti_pdf ?? false,
  regole: { tutti_i_turni: e.regole?.tutti_i_turni ?? true, lezione_una_volta_al_giorno: e.regole?.lezione_una_volta_al_giorno ?? true },
});

/** Una sezione delle impostazioni: a sinistra cosa e' e a cosa serve, a destra i campi. */
function Sezione({ icona: Icona, titolo, sotto, children }: { icona: typeof Mail; titolo: string; sotto: string; children: ReactNode }) {
  return (
    <section className="grid gap-4 border-t border-gray-100 py-6 first:border-t-0 first:pt-0 last:pb-0 md:grid-cols-[15rem_minmax(0,1fr)] md:gap-8">
      <div className="flex gap-3 md:block">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-neutral-600 md:mb-3">
          <Icona className="h-4 w-4" />
        </span>
        <div>
          <h2 className="text-sm font-semibold text-gray-900">{titolo}</h2>
          <p className="mt-1 text-xs leading-relaxed text-neutral-500">{sotto}</p>
        </div>
      </div>
      <div className="min-w-0 space-y-4">{children}</div>
    </section>
  );
}

function CampoTesto(props: {
  id: string;
  etichetta: string;
  spiegazione?: string;
  valore: string | null;
  segnaposto?: string;
  tipo?: string;
  onCambia: (v: string) => void;
}) {
  return (
    <div>
      <Label htmlFor={props.id} className="text-xs">
        {props.etichetta}
      </Label>
      <Input
        id={props.id}
        type={props.tipo ?? 'text'}
        value={props.valore ?? ''}
        placeholder={props.segnaposto}
        onChange={(e) => props.onCambia(e.target.value)}
        className="mt-1"
      />
      {props.spiegazione && <p className="mt-1 text-[11px] leading-relaxed text-neutral-500">{props.spiegazione}</p>}
    </div>
  );
}

/**
 * I numeri dei prodotti WooCommerce, scritti a mano. Il testo resta com'e'
 * mentre si scrive; quando si esce dal campo diventa l'elenco di numeri (o
 * resta con l'errore sotto, se non lo e').
 */
function CampoProdotti({ valore, onCambia }: { valore: number[]; onCambia: (v: number[]) => void }) {
  const [testo, setTesto] = useState(valore.join(', '));
  const [sbagliato, setSbagliato] = useState(false);
  const salvato = valore.join(', ');
  useEffect(() => {
    setTesto(salvato);
    setSbagliato(false);
  }, [salvato]);

  const conferma = () => {
    const esito = leggiProdotti(testo);
    setSbagliato(!esito.ok);
    if (esito.ok) {
      onCambia(esito.prodotti);
      setTesto(esito.prodotti.join(', '));
    }
  };

  return (
    <div>
      <Label htmlFor="ev-prodotti" className="text-xs">
        Numeri dei prodotti WooCommerce
      </Label>
      <Input id="ev-prodotti" value={testo} placeholder="Es. 1022" inputMode="numeric" className="mt-1"
        onChange={(e) => setTesto(e.target.value)} onBlur={conferma} aria-invalid={sbagliato} />
      {sbagliato ? (
        <p className="mt-1 text-[11px] font-medium text-red-600">Solo numeri, separati da una virgola (es. 1022, 1023).</p>
      ) : (
        <p className="mt-1 text-[11px] leading-relaxed text-neutral-500">
          Su WooCommerce, in Prodotti, è il numero «ID» che compare passando sul nome. Ogni posto comprato di questi prodotti
          diventa un biglietto da registrare.
        </p>
      )}
    </div>
  );
}

/**
 * Il modulo delle impostazioni, diviso per argomento: l'evento, chi
 * partecipa, la pagina di scelta, l'app, i biglietti dal sito, le email
 * automatiche. Il pulsante per salvare compare in basso, fisso, appena si
 * cambia qualcosa.
 */
export function ModuloImpostazioni({ evento, etichette, onSalva }: Props) {
  const [campi, setCampi] = useState<CampiImpostazioni>(() => daEvento(evento));
  const [salvando, setSalvando] = useState(false);
  const [tentato, setTentato] = useState(false);

  // Dopo un salvataggio si riparte da quello che c'e' nel database. Solo se
  // cambiano proprio questi campi: importare il programma, cambiare i posti o
  // aprire le richieste rilegge l'evento, ma non deve cancellare quello che si
  // sta scrivendo.
  const salvato = JSON.stringify(daEvento(evento));
  useEffect(() => {
    setCampi(JSON.parse(salvato) as CampiImpostazioni);
    setTentato(false);
  }, [salvato]);

  const errori = useMemo(() => validaImpostazioni({ ...campi, scelte_aperte: evento.scelte_aperte }), [campi, evento.scelte_aperte]);
  const modificato = JSON.stringify(campi) !== salvato;
  const cambia = <K extends keyof CampiImpostazioni>(chiave: K, valore: CampiImpostazioni[K]) =>
    setCampi((prima) => ({ ...prima, [chiave]: valore }));
  const cambiaRegola = (chiave: keyof CampiImpostazioni['regole'], valore: boolean) =>
    setCampi((prima) => ({ ...prima, regole: { ...prima.regole, [chiave]: valore } }));

  const salva = async () => {
    setTentato(true);
    if (errori.length > 0) return;
    setSalvando(true);
    try {
      await onSalva(campi);
    } finally {
      setSalvando(false);
    }
  };

  const annulla = () => {
    setCampi(JSON.parse(salvato) as CampiImpostazioni);
    setTentato(false);
  };

  return (
    <div className="space-y-4">
      <Pannello>
        <Sezione icona={Ticket} titolo="Evento" sotto="Il nome con cui lo vedete in Pixel.">
          <CampoTesto id="ev-nome" etichetta="Nome" segnaposto="Es. Congresso HS&UT 2026" valore={campi.nome} onCambia={(v) => cambia('nome', v)} />
        </Sezione>

        <Sezione
          icona={Users}
          titolo="Partecipanti"
          sotto="Chi è nella lista diventa partecipante e riceve il suo link personale. Chi entra nella lista dopo viene aggiunto da solo."
        >
          <SceltaEtichetta id="ev-lista" etichetta="Lista dei partecipanti" valore={campi.lista_id} opzioni={etichette}
            onCambia={(v) => cambia('lista_id', v)} spiegazione="Le liste sono quelle di Mailing." />
        </Sezione>

        <Sezione
          icona={MousePointerClick}
          titolo="Pagina di scelta"
          sotto="La pagina del sito dove ognuno sceglie il suo percorso. Ci arriva dal pulsante con il link personale nelle email."
        >
          <div className="grid gap-4 lg:grid-cols-2">
            <CampoTesto id="ev-url-scelta" etichetta="Indirizzo della pagina" valore={campi.url_scelta} segnaposto="https://…"
              onCambia={(v) => cambia('url_scelta', v)} spiegazione="Deve iniziare con https://" />
            <CampoTesto id="ev-email" tipo="email" etichetta="Email di riferimento" valore={campi.email_riferimento} segnaposto="eventi@…"
              onCambia={(v) => cambia('email_riferimento', v)} spiegazione="A chi scrive chi vuole cambiare la sua richiesta: la pagina la mostra dopo l'invio." />
          </div>
          <div className="space-y-4 rounded-lg border border-gray-100 bg-gray-50/70 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Regole di scelta</p>
            <Interruttore acceso={campi.regole.tutti_i_turni} onCambia={(v) => cambiaRegola('tutti_i_turni', v)} etichetta="Tutti i turni sono obbligatori"
              spiegazione="La persona può inviare la richiesta solo dopo aver scelto un incontro in ogni turno di ogni giorno." />
            <Interruttore acceso={campi.regole.lezione_una_volta_al_giorno} onCambia={(v) => cambiaRegola('lezione_una_volta_al_giorno', v)}
              etichetta="Una lezione ripetuta si segue una volta sola al giorno"
              spiegazione="Se la stessa lezione si ripete in più turni (es. Sala A), dopo averla scelta sparisce dagli altri turni dello stesso giorno. Vale anche per le modifiche che fate a mano qui in Pixel." />
          </div>
        </Sezione>

        <Sezione icona={Smartphone} titolo="App dell'evento" sotto="Dove la persona vede il suo percorso assegnato, il giorno dell'evento.">
          <CampoTesto id="ev-url-app" etichetta="Indirizzo dell'app" valore={campi.url_app} segnaposto="https://…"
            onCambia={(v) => cambia('url_app', v)} spiegazione="Deve iniziare con https://" />
        </Sezione>

        <Sezione
          icona={ShoppingCart}
          titolo="Biglietti dal sito"
          sotto="I biglietti comprati su slabsrl.com: Pixel legge gli ordini ogni 10 minuti. Chi ha pagato riceve il link per registrare chi userà ogni biglietto; chi si registra entra nella lista dei partecipanti."
        >
          <div className="grid gap-4 lg:grid-cols-2">
            <CampoProdotti valore={campi.prodotti_sito} onCambia={(v) => cambia('prodotti_sito', v)} />
            <div>
              <CampoTesto id="ev-url-registrazione" etichetta="Pagina dei biglietti" valore={campi.url_registrazione} segnaposto="https://…"
                onCambia={(v) => cambia('url_registrazione', v)} spiegazione="Dove porta il pulsante «Registra i partecipanti» nelle email." />
              {!campi.url_registrazione && (
                <button type="button" onClick={() => cambia('url_registrazione', `${window.location.origin}/registrazione-evento`)}
                  className="mt-1 text-[11px] font-medium text-primary-700 underline underline-offset-2 hover:text-primary-800">
                  Usa la pagina di Pixel
                </button>
              )}
            </div>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50/70 p-4">
            <Interruttore acceso={campi.biglietti_pdf} onCambia={(v) => cambia('biglietti_pdf', v)}
              etichetta="Manda i biglietti in PDF dopo la registrazione"
              spiegazione="Entro 5 minuti dalla registrazione il partecipante riceve per email il biglietto con nome e azienda (copia a chi ha pagato, se è un'altra persona). Accendendolo partono anche i biglietti già registrati." />
          </div>
        </Sezione>

        <Sezione
          icona={Mail}
          titolo="Email automatiche"
          sotto="Al momento giusto Pixel mette da solo un'etichetta di Mailing: ognuna fa partire la sequenza che la aspetta."
        >
          <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {[
              { id: 'ev-richiesta', chiave: 'etichetta_richiesta_id' as const, quando: 'Quando la persona invia la sua scelta',
                spiegazione: 'Serve, per esempio, a non mandare il promemoria a chi ha già scelto.' },
              { id: 'ev-assegnata', chiave: 'etichetta_assegnata_id' as const, quando: "Quando confermate l'assegnazione",
                spiegazione: "Fa partire l'email con il percorso assegnato." },
              { id: 'ev-rimanda', chiave: 'etichetta_rimanda_link_id' as const, quando: 'Quando la persona chiede di nuovo il suo link',
                spiegazione: "Dall'app dell'evento: fa partire l'email che glielo rimanda." },
              { id: 'ev-acquisto', chiave: 'etichetta_acquisto_id' as const, quando: 'Quando qualcuno compra biglietti sul sito',
                spiegazione: "Va a chi ha pagato: fa partire l'email «Registra i partecipanti»." },
              { id: 'ev-registrati', chiave: 'etichetta_registrati_id' as const, quando: 'Quando chi ha comprato ha registrato tutti i biglietti',
                spiegazione: 'Serve a fermare i promemoria per la registrazione.' },
            ].map((r) => (
              <div key={r.id} className="p-3 md:p-4">
                <SceltaEtichetta disposizione="riga" id={r.id} etichetta={r.quando} spiegazione={r.spiegazione}
                  valore={campi[r.chiave]} opzioni={etichette} onCambia={(v) => cambia(r.chiave, v)} />
              </div>
            ))}
          </div>
        </Sezione>
      </Pannello>

      {tentato && errori.length > 0 && (
        <div className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-xs text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <ul className="space-y-1">
            {errori.map((e) => <li key={e}>{e}</li>)}
          </ul>
        </div>
      )}

      {modificato && (
        // Resta in vista in fondo mentre si scorre. Lo spazio per la barra di
        // navigazione del telefono lo lascia gia' il margine della pagina.
        <div className="sticky bottom-3 z-20 md:bottom-4">
          <div className="flex flex-col gap-3 rounded-xl bg-gray-900 px-4 py-3 text-white shadow-2xl sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm">Hai modifiche non salvate</p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={annulla} disabled={salvando} className="text-white hover:bg-white/10 hover:text-white">
                Annulla
              </Button>
              <Button onClick={salva} disabled={salvando} className="bg-primary-600 text-white hover:bg-primary-500">
                {salvando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Salva le impostazioni
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
